import React from "react";
import { Helmet } from "react-helmet-async";

export default function SanteEquilibre() {
	const titlePage =
		"Santé Équilibre | Site praticien bien-être & médecine alternative | Weblax Studio";
	const description =
		"Weblax a conçu le site de Santé Équilibre, praticien bien-être et médecine alternative : design apaisant, prise de rendez-vous en ligne, SEO local et pages de présentation des soins. +80% de nouvelles prises de contact en 6 semaines.";
	const url = "https://weblax.fr/realisations/sante-equilibre";

	const features = [
		{
			icon: "📅",
			title: "Prise de RDV en ligne",
			desc: "Système de réservation intégré avec calendrier temps réel, confirmation automatique par email et SMS, rappel de rendez-vous 24h avant — pour réduire les no-shows et libérer du temps administratif.",
		},
		{
			icon: "🌿",
			title: "Présentation des soins",
			desc: "Pages détaillées pour chaque pratique proposée : naturopathie, ostéopathie, réflexologie, hypnothérapie. Contenu pédagogique rassurant qui répond aux questions avant même le premier contact.",
		},
		{
			icon: "👤",
			title: "Profil praticien authentique",
			desc: "Page de présentation du praticien centrée sur la confiance : parcours, formations, certifications, philosophie de soin. Pour que le patient se sente en sécurité avant de prendre rendez-vous.",
		},
		{
			icon: "📍",
			title: "SEO local bien-être",
			desc: "Référencement ciblé sur les requêtes naturopathe, ostéopathe, praticien bien-être dans la zone géographique — avec balisage schema.org HealthAndBeautyBusiness et LocalBusiness.",
		},
		{
			icon: "💬",
			title: "Témoignages patients",
			desc: "Section avis et témoignages structurée pour rassurer les visiteurs hésitants. La preuve sociale est particulièrement déterminante dans le secteur de la santé alternative.",
		},
		{
			icon: "📱",
			title: "Accessibilité & mobile",
			desc: "Site entièrement responsive, rapide et accessible — pour les personnes de tous âges qui consultent souvent depuis leur smartphone, parfois dans des moments de vulnérabilité.",
		},
	];

	const results = [
		{
			icon: "📅",
			metric: "+80%",
			label: "Nouvelles prises de contact",
			color: "text-teal-600",
			bg: "from-teal-50 to-cyan-50",
			border: "border-teal-100",
		},
		{
			icon: "🔍",
			metric: "Top 3",
			label: "SEO local naturopathe",
			color: "text-emerald-600",
			bg: "from-emerald-50 to-green-50",
			border: "border-emerald-100",
		},
		{
			icon: "📉",
			metric: "-60%",
			label: "Temps admin rendez-vous",
			color: "text-blue-600",
			bg: "from-blue-50 to-sky-50",
			border: "border-blue-100",
		},
		{
			icon: "⭐",
			metric: "4.9/5",
			label: "Note avis Google",
			color: "text-amber-600",
			bg: "from-amber-50 to-yellow-50",
			border: "border-amber-100",
		},
	];

	return (
		<>
			<Helmet>
				<title>{titlePage}</title>
				<meta name="description" content={description} />
				<link rel="canonical" href={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={titlePage} />
				<meta property="og:description" content={description} />
				<meta property="og:url" content={url} />
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: "Santé Équilibre — Site praticien bien-être",
						description,
						url,
						dateCreated: "2024",
						provider: {
							"@type": "Organization",
							name: "Weblax",
							url: "https://weblax.fr",
						},
						about: {
							"@type": "HealthAndBeautyBusiness",
							name: "Santé Équilibre",
							description: "Praticien bien-être et médecine alternative",
						},
					})}
				</script>
			</Helmet>

			{/* ── Hero ── */}
			<section className="relative overflow-hidden bg-gradient-to-br from-teal-50 via-white to-emerald-50 border-b border-border">
				<div
					className="absolute inset-0 opacity-20"
					style={{
						backgroundImage:
							"radial-gradient(circle at 15% 60%, #ccfbf1 0%, transparent 50%), radial-gradient(circle at 85% 25%, #d1fae5 0%, transparent 50%)",
					}}
				/>
				<div className="weblax-container py-20 relative">
					<div className="flex flex-wrap items-center gap-3 mb-6">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-teal-100 text-teal-700 border border-teal-200">
							🌿 Bien-être & santé
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
							✅ Projet livré
						</span>
						<span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
							📅 Prise de RDV en ligne
						</span>
					</div>

					<h1 className="weblax-heading-display mb-6 max-w-4xl">
						Santé Équilibre — Le site qui{" "}
						<span className="text-teal-600">inspire confiance</span> et remplit
						l'agenda
					</h1>

					<p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-10">
						Weblax a conçu le site vitrine de Santé Équilibre, praticien
						bien-être spécialisé en médecine alternative. Un projet où la
						confiance est au cœur de tout — chaque choix de design, de contenu
						et de fonctionnalité a été pensé pour rassurer le patient et lui
						donner envie de franchir le pas.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						{results.map((r) => (
							<div
								key={r.label}
								className="bg-white/80 backdrop-blur border border-border rounded-xl p-5 text-center shadow-sm"
							>
								<div className="text-2xl mb-1">{r.icon}</div>
								<div className={`text-3xl font-bold mb-1 ${r.color}`}>
									{r.metric}
								</div>
								<div className="text-xs text-muted-foreground">{r.label}</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<main className="weblax-container py-20 space-y-24">
				{/* ── Contexte ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Contexte
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Un praticien compétent, difficile à trouver en ligne
					</h2>
					<div className="grid md:grid-cols-2 gap-10">
						<div className="space-y-4 text-muted-foreground leading-relaxed">
							<p>
								Santé Équilibre est un cabinet de praticien bien-être proposant
								des soins en naturopathie, réflexologie et accompagnement
								holistique. Une expertise reconnue par ses patients fidèles —
								mais une visibilité digitale quasi nulle pour attirer de
								nouveaux clients.
							</p>
							<p>
								Dans le secteur de la santé alternative, la confiance est le
								premier critère de choix. Un patient qui cherche un naturopathe
								en ligne ne réserve pas avec le premier résultat venu — il
								évalue, compare, lit les avis, cherche à comprendre l'approche
								du praticien. Un site mal conçu ou absent est synonyme de perte
								immédiate de crédibilité.
							</p>
							<p>
								La mission de Weblax : créer un site qui traduit l'expertise et
								la bienveillance du praticien, rassure les patients hésitants et
								simplifie la prise de rendez-vous.
							</p>
						</div>
						<div className="space-y-3">
							<div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
								Situation avant le site
							</div>
							{[
								{
									label: "Présence en ligne",
									value: "Quasi inexistante",
									color: "text-red-500",
								},
								{
									label: "Prise de RDV",
									value: "Par téléphone uniquement",
									color: "text-orange-500",
								},
								{
									label: "Visibilité SEO locale",
									value: "Hors top 50",
									color: "text-red-500",
								},
								{
									label: "Présentation des soins",
									value: "Inexistante en ligne",
									color: "text-red-500",
								},
								{
									label: "Gestion administrative",
									value: "Chronophage et manuelle",
									color: "text-orange-500",
								},
							].map((item) => (
								<div
									key={item.label}
									className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30"
								>
									<span className="text-sm text-muted-foreground">
										{item.label}
									</span>
									<span className={`text-sm font-semibold ${item.color}`}>
										{item.value}
									</span>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Brief ── */}
				<section className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 rounded-2xl p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Objectifs
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">
						Ce que Santé Équilibre attendait
					</h2>
					<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{[
							{
								icon: "🛡️",
								text: "Un site qui inspire immédiatement confiance et professionnalisme",
							},
							{
								icon: "📅",
								text: "Un système de prise de RDV en ligne simple et rassurant",
							},
							{
								icon: "🌿",
								text: "Des pages de présentation claires pour chaque type de soin",
							},
							{
								icon: "📍",
								text: "Apparaître sur Google quand un patient cherche localement",
							},
							{
								icon: "⭐",
								text: "Mettre en avant les avis et témoignages de patients satisfaits",
							},
							{
								icon: "⏱️",
								text: "Réduire le temps passé à gérer les appels et rendez-vous",
							},
						].map((item) => (
							<div
								key={item.text}
								className="flex items-start gap-3 bg-white rounded-xl p-4 border border-teal-100 shadow-sm"
							>
								<span className="text-xl shrink-0">{item.icon}</span>
								<span className="text-sm text-muted-foreground leading-relaxed">
									{item.text}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Approche ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Notre approche
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">La confiance avant tout</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Pour un praticien de santé alternative, le site web doit surmonter
						une barrière particulière : le scepticisme. Un visiteur qui arrive
						sur le site porte souvent des doutes — sur l'efficacité des soins,
						sur les qualifications du praticien, sur la légitimité de la
						démarche. Chaque élément du site doit contribuer à lever ces doutes
						et créer un sentiment de sécurité.
					</p>
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{features.map((f) => (
							<div
								key={f.title}
								className="group p-6 rounded-2xl border border-border hover:border-teal-200 hover:shadow-md transition-all duration-200 bg-white"
							>
								<span className="text-3xl mb-4 block">{f.icon}</span>
								<h3 className="font-semibold text-foreground mb-2">
									{f.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{f.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Processus ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Déroulement
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-10">
						5 semaines, de l'échange initial au lancement
					</h2>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								step: "01",
								title: "Découverte & storytelling",
								desc: "Entretien approfondi avec le praticien pour comprendre son parcours, sa philosophie de soin, ses patients cibles et les messages clés à transmettre.",
								tag: "Semaine 1",
							},
							{
								step: "02",
								title: "Architecture & design",
								desc: "Structure du site pensée pour le parcours patient : accueil rassurant → découverte des soins → profil praticien → témoignages → prise de RDV.",
								tag: "Semaine 2",
							},
							{
								step: "03",
								title: "Contenu & pages soins",
								desc: "Rédaction des contenus pour chaque soin proposé : qu'est-ce que c'est, pour qui, comment ça se passe, ce qu'on peut en attendre — pédagogique et rassurant.",
								tag: "Semaine 3",
							},
							{
								step: "04",
								title: "Développement & RDV en ligne",
								desc: "Intégration du système de réservation, développement des pages, optimisation mobile et mise en place du SEO local.",
								tag: "Semaines 4-5",
							},
						].map((item) => (
							<div
								key={item.step}
								className="p-6 rounded-2xl border border-border bg-white"
							>
								<div className="flex items-center justify-between mb-3">
									<span className="text-2xl font-bold text-teal-200">
										{item.step}
									</span>
									<span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
										{item.tag}
									</span>
								</div>
								<h3 className="font-semibold text-foreground mb-2">
									{item.title}
								</h3>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{item.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Stack ── */}
				<section className="bg-muted/30 rounded-2xl border border-border p-10">
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Stack technique
						</span>
					</div>
					<h2 className="text-2xl font-bold mb-8">Technologies utilisées</h2>
					<div className="flex flex-wrap gap-3">
						{[
							{ name: "Next.js", desc: "SSR & SEO" },
							{ name: "React", desc: "Interface" },
							{ name: "Tailwind CSS", desc: "Design system" },
							{ name: "Cal.com", desc: "Réservation en ligne" },
							{ name: "Schema.org", desc: "HealthAndBeautyBusiness" },
							{ name: "Nodemailer", desc: "Confirmations RDV" },
							{ name: "Vercel", desc: "Déploiement" },
						].map((tech) => (
							<div
								key={tech.name}
								className="flex items-center gap-2 bg-white border border-border rounded-lg px-4 py-2.5 shadow-sm"
							>
								<span className="font-semibold text-sm text-foreground">
									{tech.name}
								</span>
								<span className="text-xs text-muted-foreground">
									— {tech.desc}
								</span>
							</div>
						))}
					</div>
				</section>

				{/* ── Résultats ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Impact
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-4">
						Un agenda qui se remplit tout seul
					</h2>
					<p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
						Six semaines après le lancement, Santé Équilibre reçoit davantage de
						nouvelles prises de contact que lors des six mois précédents. Le
						système de RDV en ligne travaille 24h/24, même pendant les séances.
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								metric: "+80%",
								detail: "de nouvelles prises de contact",
								desc: "Le site génère un flux régulier de nouveaux patients — des personnes qui ont trouvé Santé Équilibre sur Google et ont été rassurées par le contenu avant de réserver.",
								color: "text-teal-600",
								bg: "from-teal-50 to-cyan-50",
								border: "border-teal-100",
							},
							{
								metric: "Top 3",
								detail: "SEO local naturopathe",
								desc: "Positionnement Google atteint sur les requêtes naturopathe et praticien bien-être dans la zone géographique cible — devant des cabinets établis depuis des années.",
								color: "text-emerald-600",
								bg: "from-emerald-50 to-green-50",
								border: "border-emerald-100",
							},
							{
								metric: "-60%",
								detail: "de temps administratif RDV",
								desc: "La prise de RDV en ligne automatisée avec confirmations et rappels a libéré plus d'une heure par jour — du temps restitué aux soins et aux patients.",
								color: "text-blue-600",
								bg: "from-blue-50 to-sky-50",
								border: "border-blue-100",
							},
							{
								metric: "4.9/5",
								detail: "de note Google moyenne",
								desc: "La qualité perçue du praticien — renforcée par un site professionnel qui inspire confiance — a eu un effet positif sur le nombre et la qualité des avis laissés.",
								color: "text-amber-600",
								bg: "from-amber-50 to-yellow-50",
								border: "border-amber-100",
							},
						].map((r) => (
							<div
								key={r.metric}
								className={`bg-gradient-to-br ${r.bg} border ${r.border} rounded-2xl p-8`}
							>
								<div className={`text-5xl font-bold mb-2 ${r.color}`}>
									{r.metric}
								</div>
								<div className="font-semibold text-foreground mb-3">
									{r.detail}
								</div>
								<p className="text-sm text-muted-foreground leading-relaxed">
									{r.desc}
								</p>
							</div>
						))}
					</div>
				</section>

				{/* ── Citation ── */}
				<section className="border border-teal-100 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-10 text-center">
					<span className="text-4xl mb-6 block">🌿</span>
					<blockquote className="text-xl font-medium text-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
						"Avant, je dépendais entièrement du bouche-à-oreille. Maintenant, de
						nouveaux patients arrivent chaque semaine en disant qu'ils ont lu
						mon site et qu'ils se sont tout de suite sentis en confiance. C'est
						exactement ce que je voulais."
					</blockquote>
					<cite className="text-sm text-muted-foreground not-italic">
						Praticien fondateur — Santé Équilibre
					</cite>
				</section>

				{/* ── Conclusion + CTA ── */}
				<section>
					<div className="flex items-center gap-3 mb-6">
						<span className="w-8 h-px bg-teal-500" />
						<span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">
							Conclusion
						</span>
					</div>
					<h2 className="text-3xl font-bold mb-6">
						Pour un praticien bien-être, le site web est son premier soin
					</h2>
					<div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed mb-12">
						<p>
							Santé Équilibre démontre qu'un praticien de médecine alternative
							peut transformer sa présence digitale en véritable levier
							d'acquisition — à condition que le site réponde à la vraie
							question du visiteur : "Puis-je faire confiance à cette personne
							?"
						</p>
						<p>
							La combinaison design apaisant + contenu pédagogique + avis
							patients + SEO local crée une chaîne de confiance qui guide
							naturellement le visiteur vers la prise de rendez-vous.
						</p>
						<p>
							C'est cette approche que Weblax applique systématiquement pour les
							praticiens de santé et de bien-être — parce que dans ce secteur,
							la confiance n'est pas un bonus, c'est le produit lui-même.
						</p>
					</div>

					<div className="bg-foreground text-background rounded-2xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
						<div>
							<h3 className="text-xl font-bold mb-2">
								Votre cabinet mérite un site qui inspire confiance
							</h3>
							<p className="text-sm opacity-70">
								Site praticien, prise de RDV en ligne, SEO santé — parlons de
								votre projet.
							</p>
						</div>
						<a
							href="/contact"
							className="shrink-0 bg-white text-foreground font-semibold px-8 py-3 rounded-xl hover:bg-teal-50 transition-colors text-sm whitespace-nowrap"
						>
							Démarrer un projet →
						</a>
					</div>
				</section>
			</main>
		</>
	);
}
